import { PLANNED_ARTICLES } from '@hoopoe/shared';
import { BookOpen } from 'lucide-react';
import { useEffect } from 'react';
import { articleBySlug, GLOSSARY, GLOSSARY_SLUG } from '../../lib/help/articles';
import { useCustomArticlesState } from '../../lib/help/custom';
import { DataText } from '../data/data-text';
import { EmptyState } from '../guidance/empty-state';
import { useChecklist } from './checklist';
import { HelpFeedback } from './feedback';
import { Markdown } from './markdown';

export function ArticleView({
  slug,
  compact = false,
  onNavigate,
}: {
  slug: string;
  compact?: boolean;
  onNavigate?: (slug: string, hash?: string) => void;
}) {
  const { complete } = useChecklist();
  const custom = useCustomArticlesState();
  // biome-ignore lint/correctness/useExhaustiveDependencies: mark once per article view
  useEffect(() => {
    if (slug === 'core-concepts') complete('core-concepts');
  }, [slug]);

  if (slug === GLOSSARY_SLUG) return <GlossaryView compact={compact} onNavigate={onNavigate} />;
  const article = articleBySlug(slug, custom.articles);
  if (!article && custom.pending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!article) {
    const task = PLANNED_ARTICLES[slug];
    return (
      <EmptyState icon={BookOpen} title={task ? 'This guide is on its way' : 'Article not found'}>
        {task ? `It is written together with the feature it describes (build task ${task}).` : 'Try the search above.'}
      </EmptyState>
    );
  }
  return (
    <article aria-labelledby="article-title" className="space-y-4">
      <header>
        <h1 id="article-title" className={compact ? 'text-lg font-semibold' : 'text-2xl font-semibold tracking-tight'}>
          {article.title}
        </h1>
        {article.summary && <p className="mt-1 text-sm text-muted-foreground">{article.summary}</p>}
        {article.custom && (
          <p className="mt-1 text-xs text-muted-foreground">
            Written by your admins
            {article.custom.updatedByName && (
              <>
                {' '}
                · <DataText>{article.custom.updatedByName}</DataText>
              </>
            )}{' '}
            · updated {new Date(article.custom.updatedAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
          </p>
        )}
      </header>
      <Markdown source={article.body} onInternalLink={onNavigate} dir={article.custom ? 'auto' : undefined} />
      {!!article.related?.length && (
        <nav aria-label="Related articles" className="text-sm">
          <h2 className="mb-1 font-semibold">Related</h2>
          <ul className="flex flex-wrap gap-2">
            {article.related.map(r => {
              const title = r === GLOSSARY_SLUG ? 'Glossary' : articleBySlug(r, custom.articles)?.title;
              return title ? (
                <li key={r}>
                  <a
                    href={`/onboarding/${r}`}
                    className="rounded-full border px-2.5 py-0.5 hover:bg-muted"
                    onClick={e => {
                      if (onNavigate) {
                        e.preventDefault();
                        onNavigate(r);
                      }
                    }}
                  >
                    {title}
                  </a>
                </li>
              ) : null;
            })}
          </ul>
        </nav>
      )}
      <HelpFeedback key={article.slug} slug={article.slug} />
    </article>
  );
}

function GlossaryView({ compact, onNavigate }: { compact: boolean; onNavigate?: (slug: string, hash?: string) => void }) {
  const letters = [...new Set(GLOSSARY.map(g => g.term[0]?.toUpperCase()))];
  return (
    <article aria-labelledby="article-title" className="space-y-4">
      <h1 id="article-title" className={compact ? 'text-lg font-semibold' : 'text-2xl font-semibold tracking-tight'}>
        Glossary
      </h1>
      <p className="text-sm text-muted-foreground">Every term, A–Z, with a link to the article that explains it.</p>
      {letters.map(letter => (
        <section key={letter} aria-labelledby={`letter-${letter}`}>
          <h2 id={`letter-${letter}`} className="mt-4 border-b pb-1 text-sm font-semibold text-muted-foreground">
            {letter}
          </h2>
          <dl className="divide-y text-sm">
            {GLOSSARY.filter(g => g.term[0]?.toUpperCase() === letter).map(g => (
              <div key={g.id} id={g.id} className="scroll-mt-20 py-2">
                <dt className="font-medium">{g.term}</dt>
                <dd className="text-muted-foreground">
                  {g.definition}{' '}
                  {g.see && (
                    <a
                      href={`/onboarding/${g.see}`}
                      className="font-medium text-foreground underline underline-offset-2"
                      onClick={e => {
                        if (onNavigate && g.see) {
                          e.preventDefault();
                          onNavigate(g.see);
                        }
                      }}
                    >
                      More →
                    </a>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </article>
  );
}
